import { Element, type PinSlot } from './element';
import type { MnaMatrix } from './mna';
import { fmt } from './format';

/**
 * 마이크로컨트롤러 (아두이노 비슷한 것).
 * 핀: 왼쪽 VCC, GND, P0, P1 / 오른쪽 P2, P3, P4, P5
 *
 * 사용자가 쓴 JavaScript 의 setup() 을 전원이 들어올 때 한 번, loop() 를 tick 마다 부른다.
 * - 출력핀은 CMOS 푸시풀: 1 이면 VCC 쪽으로, 0 이면 GND 쪽으로 25Ω
 * - analogWrite(PWM) 은 평균 전압 모델 (듀티만큼 VCC/GND 에 나눠 연결)
 * - 입력핀은 10MΩ, 전압을 읽어 digitalRead / analogRead 로 돌려준다
 * - 전원(VCC−GND)이 1.8V 밑이면 꺼진다
 * 코드는 스텝이 확정된 뒤(afterAccept)에만 돌아서 적응형 스텝이 되돌려도 부작용이 없다.
 */

const GPIO = 6;
const R_OUT = 25;
const G_IN = 1e-7;
const I_Q = 0.01; // 동작 전류 10mA

export const DEFAULT_MCU_CODE = `// 핀 번호: P0 ~ P5 → 0 ~ 5
// pinMode(핀, 'out' | 'in')      digitalWrite(핀, 1 | 0)
// analogWrite(핀, 0~255)  PWM    digitalRead(핀) → 0 | 1
// analogRead(핀) → 0~1023        readVoltage(핀) → V
// millis() → 켜진 뒤 ms          print(...) → 아래 로그

function setup() {
  pinMode(2, 'out');
  print('안녕! 깜빡이 시작');
}

function loop() {
  // 0.5초마다 켜고 끄기
  digitalWrite(2, Math.floor(millis() / 500) % 2);
}
`;

type Mode = 'in' | 'out';

interface Program {
  setup: (() => void) | null;
  loop: (() => void) | null;
}

export class Microcontroller extends Element {
  modes: Mode[] = Array(GPIO).fill('in');
  /** 출력 듀티 0~1 (digitalWrite 는 0/1) */
  duty: number[] = Array(GPIO).fill(0);
  /** 핀 전압 (GND 기준) — 마지막으로 푼 해 */
  private pinV: number[] = Array(GPIO + 2).fill(0);
  private pinI: number[] = Array(GPIO + 2).fill(0);

  powered = false;
  error = '';
  logs: string[] = [];
  private ms = 0;
  private acc = 0;
  private program: Program | null = null;
  private compiledFor = '';
  private guardCount = 0;

  override get pinCount(): number {
    return GPIO + 2;
  }
  override get usesLayout(): boolean {
    return true;
  }
  override pinLayout(): PinSlot[] {
    const left = ['VCC', 'GND', 'P0', 'P1'];
    const right = ['P2', 'P3', 'P4', 'P5'];
    return [
      ...left.map((label, i) => ({ t: 0, s: 2 * i - 3, label })),
      ...right.map((label, i) => ({ t: 'end' as const, s: 2 * i - 3, label })),
    ];
  }
  /** GPIO 번호 → 핀 인덱스 (VCC=0, GND=1, P0=2 …) */
  private gp(i: number): number {
    return i + 2;
  }
  private tick(): number {
    return Math.max(this.num('tickMs', 1), 0.1) / 1000;
  }
  override get maxStep(): number {
    return this.tick();
  }

  override resetState(): void {
    this.modes = Array(GPIO).fill('in');
    this.duty = Array(GPIO).fill(0);
    this.pinV = Array(GPIO + 2).fill(0);
    this.pinI = Array(GPIO + 2).fill(0);
    this.powered = false;
    this.error = '';
    this.logs = [];
    this.ms = 0;
    this.acc = 0;
    this.program = null;
    this.compiledFor = '';
  }

  get vcc(): number {
    return this.pinV[0] - this.pinV[1];
  }

  /* ───────── 전기 모델 ───────── */

  override stamp(m: MnaMatrix): void {
    const vccN = this.nodes[0];
    const gndN = this.nodes[1];
    if (this.powered) m.currentSource(vccN, gndN, I_Q);
    m.conductance(vccN, gndN, 1e-7);
    const g = 1 / R_OUT;
    for (let i = 0; i < GPIO; i++) {
      const n = this.nodes[this.gp(i)];
      if (this.powered && this.modes[i] === 'out') {
        const d = Math.min(1, Math.max(0, this.duty[i]));
        if (d > 0) m.conductance(n, vccN, g * d);
        if (d < 1) m.conductance(n, gndN, g * (1 - d));
      } else {
        m.conductance(n, gndN, G_IN);
      }
    }
  }

  override endStep(m: MnaMatrix): void {
    const v = this.nodes.map((n) => m.nodeVoltage(n));
    const vcc = v[0];
    const gnd = v[1];
    this.pinV = v.map((x) => x - gnd);
    this.pinV[0] = vcc - gnd;
    this.pinV[1] = 0;
    // 핀으로 들어가는 전류 (흐름 표시용)
    const I = Array(GPIO + 2).fill(0);
    if (this.powered) {
      I[0] += I_Q;
      I[1] -= I_Q;
    }
    I[0] += 1e-7 * (vcc - gnd);
    I[1] -= 1e-7 * (vcc - gnd);
    const g = 1 / R_OUT;
    for (let i = 0; i < GPIO; i++) {
      const k = this.gp(i);
      const vp = v[k];
      if (this.powered && this.modes[i] === 'out') {
        const d = Math.min(1, Math.max(0, this.duty[i]));
        const up = g * d * (vp - vcc);
        const dn = g * (1 - d) * (vp - gnd);
        I[k] += up + dn;
        I[0] -= up;
        I[1] -= dn;
      } else {
        const i0 = G_IN * (vp - gnd);
        I[k] += i0;
        I[1] -= i0;
      }
    }
    this.pinI = I;
    this.voltageDrop = vcc - gnd;
    this.current = I[0];
  }

  override pinCurrents(): number[] {
    return this.pinI;
  }

  override get power(): number {
    return this.pinI.reduce((s, i, k) => s + i * (this.pinV[k] ?? 0), 0);
  }

  /* ───────── 프로그램 실행 ───────── */

  private log(msg: string): void {
    const t = (this.ms / 1000).toFixed(2);
    this.logs.push(`[${t}s] ${msg}`);
    if (this.logs.length > 80) this.logs.shift();
  }

  private compile(): void {
    const code = this.str('code', DEFAULT_MCU_CODE);
    this.compiledFor = code;
    this.program = null;
    this.error = '';
    // 무한루프 방지: for/while/do 블록 안에 가드 호출을 끼워 넣는다
    const guarded = code.replace(/\b(for|while)\s*\(([^)]*)\)\s*\{/g, '$1 ($2) { __guard();').replace(/\bdo\s*\{/g, 'do { __guard();');
    try {
      const factory = new Function(
        'api',
        `"use strict";
const { pinMode, digitalWrite, analogWrite, digitalRead, analogRead, readVoltage, millis, print, vcc, constrain, map, __guard } = api;
${guarded}
;return { setup: typeof setup === 'function' ? setup : null, loop: typeof loop === 'function' ? loop : null };`,
      ) as (api: unknown) => Program;
      this.program = factory(this.api());
      if (!this.program.loop && !this.program.setup) this.log('setup() 이나 loop() 함수가 없어요');
    } catch (e) {
      this.fail(e);
    }
  }

  private fail(e: unknown): void {
    this.error = e instanceof Error ? e.message : String(e);
    this.log(`⚠ 에러: ${this.error}`);
  }

  private pinIndex(p: unknown): number {
    const i = Math.floor(Number(p));
    if (!(i >= 0 && i < GPIO)) throw new Error(`핀 번호 ${String(p)} 는 없어요 (0~5)`);
    return i;
  }

  private api(): Record<string, unknown> {
    return {
      pinMode: (p: unknown, mode: unknown) => {
        const i = this.pinIndex(p);
        const m = String(mode).toLowerCase();
        this.modes[i] = m === 'out' || m === 'output' ? 'out' : 'in';
      },
      digitalWrite: (p: unknown, v: unknown) => {
        const i = this.pinIndex(p);
        this.modes[i] = 'out';
        this.duty[i] = v && v !== '0' ? 1 : 0;
      },
      analogWrite: (p: unknown, v: unknown) => {
        const i = this.pinIndex(p);
        this.modes[i] = 'out';
        this.duty[i] = Math.min(255, Math.max(0, Number(v) || 0)) / 255;
      },
      digitalRead: (p: unknown) => (this.pinV[this.gp(this.pinIndex(p))] > this.vcc * 0.5 ? 1 : 0),
      analogRead: (p: unknown) => {
        const v = this.pinV[this.gp(this.pinIndex(p))];
        return Math.round(Math.min(1, Math.max(0, v / Math.max(this.vcc, 1e-6))) * 1023);
      },
      readVoltage: (p: unknown) => this.pinV[this.gp(this.pinIndex(p))],
      millis: () => Math.floor(this.ms),
      vcc: () => this.vcc,
      print: (...args: unknown[]) => this.log(args.map((a) => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' ')),
      constrain: (x: number, a: number, b: number) => Math.min(b, Math.max(a, x)),
      map: (x: number, a: number, b: number, c: number, d: number) => c + ((x - a) * (d - c)) / (b - a),
      __guard: () => {
        if (++this.guardCount > 200000) throw new Error('반복이 너무 많아요 (무한루프?) — loop() 는 매 틱마다 불리니 while(true) 는 쓰지 마세요');
      },
    };
  }

  override afterAccept(dt: number): void {
    const vcc = this.vcc;
    const wasOn = this.powered;
    this.powered = vcc >= (wasOn ? 1.8 : 2.0);
    if (!this.powered) {
      if (wasOn) this.log('전원 꺼짐 (전압 부족)');
      return;
    }
    const code = this.str('code', DEFAULT_MCU_CODE);
    if (!wasOn || code !== this.compiledFor) {
      // 전원이 새로 들어오거나 코드가 바뀌면 처음부터
      this.modes = Array(GPIO).fill('in');
      this.duty = Array(GPIO).fill(0);
      this.ms = 0;
      this.acc = 0;
      this.compile();
      if (this.program?.setup && !this.error) {
        try {
          this.guardCount = 0;
          this.program.setup();
        } catch (e) {
          this.fail(e);
        }
      }
    }
    if (this.error || !this.program?.loop) return;
    this.acc += dt;
    const tick = this.tick();
    let n = 0;
    while (this.acc >= tick - 1e-12 && n < 10000) {
      this.acc -= tick;
      this.ms += tick * 1000;
      n++;
      try {
        this.guardCount = 0;
        this.program.loop();
      } catch (e) {
        this.fail(e);
        break;
      }
    }
  }

  info(): string[] {
    const pins = Array.from({ length: GPIO }, (_, i) => {
      const v = this.pinV[this.gp(i)];
      return this.modes[i] === 'out'
        ? `P${i} 출력 ${this.duty[i] === 1 ? '1' : this.duty[i] === 0 ? '0' : `${Math.round(this.duty[i] * 100)}%`}`
        : `P${i} 입력 ${fmt(v, 'V')}`;
    });
    return [
      '마이크로컨트롤러',
      `상태 ${this.error ? '⚠ 에러' : this.powered ? `동작 중 (${(this.ms / 1000).toFixed(2)}s)` : '꺼짐 (VCC 부족)'}`,
      `VCC ${fmt(this.vcc, 'V')} · ${fmt(this.pinI[0], 'A')}`,
      ...pins,
    ];
  }
}

/** 푸시 버튼 — 누르고 있는 동안만 연결 */
export class PushButton extends Element {
  pressed = false;
  override get idealConductor(): boolean {
    return this.pressed;
  }
  override stamp(m: MnaMatrix): void {
    if (!this.pressed) m.resistor(this.nodes[0], this.nodes[1], 1e9);
  }
  protected override computeCurrent(): number {
    return this.pressed ? this.current : this.voltageDrop / 1e9;
  }
  info(): string[] {
    return ['푸시 버튼', this.pressed ? '눌림 (ON)' : '안 눌림 (OFF)', '누르고 있는 동안만 연결돼요'];
  }
}
